import React, { useRef, useCallback, useEffect } from 'react';
import { Bold, Italic, Underline, List, ListOrdered } from 'lucide-react';

const ToolbarButton = ({ onClick, active, children, title }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active 
        ? 'bg-slate-200 text-slate-900' 
        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
    }`}
  >
    {children}
  </button>
);

const RichTextEditor = ({ value, onChange, placeholder, rows = 4, "data-testid": testId }) => {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== (value || '')) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      const html = editorRef.current.innerHTML;
      onChange(html === '<br>' ? '' : html);
    }
  }, [onChange]);

  const exec = useCallback((command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    handleInput();
  }, [handleInput]);

  const minH = Math.max(rows * 24, 80);

  return (
    <div className="border border-stone-200 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-slate-400 focus-within:border-slate-400 transition-shadow">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-stone-50 border-b border-stone-200" data-testid={testId ? `${testId}-toolbar` : undefined}>
        <ToolbarButton onClick={() => exec('bold')} title="Negrita">
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec('italic')} title="Cursiva">
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec('underline')} title="Subrayado">
          <Underline className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-stone-200 mx-1" />
        <ToolbarButton onClick={() => exec('insertUnorderedList')} title="Lista">
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec('insertOrderedList')} title="Lista numerada">
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        data-testid={testId}
        data-placeholder={placeholder}
        style={{ minHeight: `${minH}px` }}
        className={`
          px-3 py-2 text-sm text-slate-900 outline-none
          [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-slate-400
          [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4
          [&_b]:font-bold [&_strong]:font-bold [&_i]:italic [&_em]:italic [&_u]:underline
        `}
      />
    </div>
  );
};

export default RichTextEditor;
